<script setup lang="ts">
import { site } from '@/i18n'
import { themes, themeState, applyTheme } from '@/theme'
import UmbBootLogo from '@/components/UmbBootLogo.vue'
import LangSwitch from '@/components/LangSwitch.vue'
</script>

<template>
  <footer class="site-footer">
    <div class="container">
      <div class="footer-grid">
        <div class="footer-brand">
          <RouterLink to="/" class="brand">
            <UmbBootLogo class="brand-icon" :size="36"/>
            <span class="brand-text">{{ site.ui.brand }}<span class="brand-suffix">{{ site.ui.brandSuffix }}</span></span>
          </RouterLink>
          <p class="brand-slogan">{{ site.footer.slogan }}</p>
          <div class="theme-toggle" role="group" :aria-label="site.ui.themeSwitch">
            <button
              v-for="t in themes"
              :key="t.key"
              type="button"
              class="theme-dot"
              :class="{ active: themeState.key === t.key }"
              :style="{ background: t.swatch }"
              :aria-label="`${site.ui.themeSwitch}: ${t.key}`"
              :aria-pressed="themeState.key === t.key"
              @click="applyTheme(t.key)"
            />
            <span class="theme-label">{{ site.ui.themeSwitch }}</span>
          </div>
          <div class="lang-toggle">
            <LangSwitch/>
          </div>
        </div>

        <div
          v-for="column in site.footer.columns"
          :key="column.title"
          class="footer-column"
        >
          <h4 class="footer-title">{{ column.title }}</h4>
          <ul class="footer-links">
            <li v-for="link in column.links" :key="link.path">
              <RouterLink :to="link.path">{{ link.label }}</RouterLink>
            </li>
          </ul>
        </div>
      </div>

      <div class="anti-fraud">
        <div class="fraud-icon">⚠️</div>
        <div class="fraud-content">
          <strong>{{ site.ui.antiFraudTitle }}</strong>
          <span v-for="(tip, idx) in site.footer.antiFraud" :key="idx" class="fraud-tip">{{ tip }}</span>
        </div>
      </div>

      <div class="footer-bottom">
        <p class="copyright">{{ site.footer.copyright }}</p>
        <p class="domain-note">{{ site.ui.domainNote }}</p>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.site-footer {
  margin-top: auto;
  background: linear-gradient(180deg, var(--u2-secondary) 0%, var(--u2-bg) 100%);
  border-top: 1px solid var(--u2-stroke);
  padding: var(--space-16) 0 var(--space-8);
}

.footer-grid {
  display: grid;
  grid-template-columns: 1.6fr repeat(3, 1fr);
  gap: var(--space-10);
  margin-bottom: var(--space-10);
}

.footer-brand .brand {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-weight: 900;
  font-size: 20px;
  margin-bottom: var(--space-4);
}

.brand-icon {
  flex-shrink: 0;
}

.brand-suffix {
  color: var(--u2-primary);
  margin-left: 2px;
}

.brand-slogan {
  color: var(--u2-muted);
  font-size: 14px;
  line-height: 1.7;
  max-width: 280px;
  margin: 0 0 var(--space-5);
}

.theme-toggle {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.theme-dot {
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  cursor: pointer;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
  opacity: 0.6;
  transition: opacity 0.2s ease, transform 0.2s ease, outline 0.2s ease;
}

.theme-dot.active {
  outline: 2px solid var(--u2-primary);
  outline-offset: 2px;
}

.theme-dot:focus-visible {
  outline: 2px solid var(--u2-primary);
  outline-offset: 2px;
}

.theme-dot.active,
.theme-dot:hover {
  opacity: 1;
  transform: scale(1.1);
}

.theme-label {
  margin-left: var(--space-2);
  font-size: 12px;
  color: var(--u2-muted);
}

.lang-toggle {
  margin-top: var(--space-4);
}

.footer-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--u2-text);
  margin: 0 0 var(--space-4);
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.footer-links a {
  color: var(--u2-muted);
  font-size: 14px;
  transition: color 0.2s ease;
}

.footer-links a:hover {
  color: var(--u2-emphasis);
}

.anti-fraud {
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
  background: rgba(199, 119, 0, 0.06);
  border: 1px solid rgba(199, 119, 0, 0.22);
  border-radius: var(--radius-lg);
  padding: var(--space-5) var(--space-6);
  margin-bottom: var(--space-8);
}

.fraud-icon {
  font-size: 24px;
  line-height: 1;
}

.fraud-content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2) var(--space-4);
  font-size: 14px;
  color: var(--u2-warning);
}

.fraud-tip {
  color: var(--u2-muted);
}

.fraud-tip::before {
  content: '•';
  margin-right: var(--space-2);
  color: var(--u2-warning);
}

.footer-bottom {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-3);
  padding-top: var(--space-6);
  border-top: 1px solid var(--u2-stroke);
  font-size: 13px;
  color: var(--u2-muted);
}

@media (max-width: 900px) {
  .footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: var(--space-8);
  }
}

@media (max-width: 600px) {
  .footer-grid {
    grid-template-columns: 1fr;
  }
  .footer-bottom {
    flex-direction: column;
    text-align: center;
  }
}
</style>
